import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { PermissionEnum } from '@Application/Security/Roles';
import ErrorState from '@Guidelines/Component/ErrorState';
import Eyebrow from '@Guidelines/Component/Eyebrow';
import { theme } from '@Guidelines/Theme/theme';
import useAccountDocumentTypes from '@UI/Hooks/useAccountDocumentTypes';
import useSubjectWorkflowRuns from '@UI/Hooks/useSubjectWorkflowRuns';
import OpenPreparationCard from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sections/OpenPreparationsSection/OpenPreparationCard';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import { css } from '@emotion/react';
import type * as React from 'react';
import { Skeleton } from '@verclam/ui-kit';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 0.75rem;

	& > *:first-child{
		padding-left: 1rem;
	}
`;

const listStyle = css`
	display: grid;
	grid-template-columns: minmax(0, 1fr) auto auto;
	gap: 0.75rem 0.875rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: 1fr;
	}
`;

const emptyNoteStyle = css`
	margin: 0;
	padding: 1.125rem 1.25rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	line-height: 1.55;
	color: ${theme.colors.inkMuted};
`;

type Props = {
	readonly subject: SubjectAddress;
	readonly isArchived: boolean;
};

/**
 * Autonomous section: this dossier's preparations still to complete, generating or to review (the
 * `inProgress` rows of the list it reads with `useSubjectWorkflowRuns`). Each card is a grid row of
 * three cells; the document chips sit on a full-width row after them.
 */
const OpenPreparationsSection: React.FC<Props> = ({ subject, isArchived }) => {
	const { data: workflowRuns, error, refetch } = useSubjectWorkflowRuns(subject);
	const { documentTypes } = useAccountDocumentTypes();
	const canWrite = useHasPermission(
		[PermissionEnum.INTERVIEW_WRITE, PermissionEnum.DOCUMENT_GENERATION_LAUNCH],
		'any',
	);
	const preparations = workflowRuns?.inProgress ?? [];

	// First render, and every render before the fetch has ever had a chance to resolve: never the
	// error state (`guides/state-stores.md`, "Ce que `useStoreQuery` ne garantit pas").
	if (workflowRuns === null && error === null) {
		return (
			<section css={sectionStyle}>
				<Eyebrow>Préparations en cours</Eyebrow>
				<Skeleton height="6rem" animation="wave" shape="rectangle" />
			</section>
		);
	}

	if (error !== null) {
		return (
			<section css={sectionStyle}>
				<Eyebrow>Préparations en cours</Eyebrow>
				<ErrorState title="Impossible de charger les préparations." onRetry={refetch} />
			</section>
		);
	}

	return (
		<section css={sectionStyle} aria-label="Préparations en cours">
			<Eyebrow>Préparations en cours</Eyebrow>
			<ConditionalLoader
				data={preparations.length > 0}
				fallback={
					<p css={emptyNoteStyle}>
						Aucune préparation en cours. « Commencer des documents » en ouvre une.
					</p>
				}
			>
				<div css={listStyle}>
					{preparations.map((preparation) => (
						<OpenPreparationCard
							key={preparation.id}
							subject={subject}
							preparation={preparation}
							documentTypes={documentTypes}
							canContinue={canWrite && !isArchived}
						/>
					))}
				</div>
			</ConditionalLoader>
		</section>
	);
};

export default OpenPreparationsSection;
